<script setup lang="ts">
import { AlertTriangle, CheckCircle2, Clock, Copy, HelpCircle, XCircle } from 'lucide-vue-next'
import DnsDiagnosticSummary from './DnsDiagnosticSummary.vue'

import type {
  DnsDiagnostic,
  DnsDiagnosticHint,
  DnsDiagnosticRecord,
  DnsDiagnosticState,
  DnsRecord,
  DnsRecordsResponse,
  EmailDomain
} from '@/composables/domainConfig'
import type { ChipColor } from '@/components/base/BaseChip.vue'

type DisplayRow = DnsRecord & {
  state: DnsDiagnosticState | null
  hint: DnsDiagnosticHint | null
}

type Tone = { bg: string; title: string; text: string; icon: string }

const props = defineProps<{
  domain: string
  dnsRecords: DnsRecordsResponse | null
  domainStatus: EmailDomain | null
  showStatus?: boolean
}>()

defineEmits<{
  (e: 'back'): void
  (e: 'finish'): void
}>()

const { t } = useI18n()
const notification = useNotification()
const { getDnsDiagnostic } = useDomainConfig()

const isVerified = computed(() => props.domainStatus?.is_ready_for_sending ?? false)
const shouldDiagnose = computed(() => !!props.showStatus && !isVerified.value && !!props.domainStatus?.id)

const diagnostic = ref<DnsDiagnostic | null>(null)
const isDiagnosing = ref(false)
const diagnosticFailed = ref(false)

const runDiagnostic = async () => {
  if (!props.domainStatus?.id) return

  isDiagnosing.value = true
  diagnosticFailed.value = false
  const result = await getDnsDiagnostic(props.domainStatus.id)
  diagnostic.value = result
  diagnosticFailed.value = result === null
  isDiagnosing.value = false
}

onMounted(() => {
  if (shouldDiagnose.value) runDiagnostic()
})

const providerName = computed(
  () => diagnostic.value?.provider?.name ?? t('pages.domain.config.steps.dns.diagnostic.yourDnsHost')
)

const normalizeHostname = (hostname: string): string => hostname.toLowerCase().replace(/\.$/, '')

const diagnosticByHostname = computed(() => {
  const map = new Map<string, DnsDiagnosticRecord>()
  diagnostic.value?.records.forEach((record) => map.set(normalizeHostname(record.hostname), record))
  return map
})

const STATE_SEVERITY: DnsDiagnosticState[] = ['ok', 'propagating', 'unknown', 'mismatch', 'missing']

const worstState = (rows: DisplayRow[]): DnsDiagnosticState | null =>
  rows.reduce<DnsDiagnosticState | null>((worst, row) => {
    if (!row.state) return worst
    if (!worst) return row.state
    return STATE_SEVERITY.indexOf(row.state) > STATE_SEVERITY.indexOf(worst) ? row.state : worst
  }, null)

const toDisplayRow = (row: DnsRecord): DisplayRow => {
  const found = diagnosticByHostname.value.get(normalizeHostname(row.hostname))
  return { ...row, state: found?.state ?? null, hint: found?.hint ?? null }
}

const recordGroups = computed(() => {
  if (props.dnsRecords?.records) {
    const { spf, dkim, dmarc, return_path } = props.dnsRecords.records
    return [
      { key: 'dkim', rows: dkim ?? [], verified: props.domainStatus?.dkim_verified ?? false },
      { key: 'spf', rows: spf ? [spf] : [], verified: props.domainStatus?.spf_verified ?? false },
      { key: 'dmarc', rows: dmarc ? [dmarc] : [], verified: props.domainStatus?.dmarc_verified ?? false },
      {
        key: 'return_path',
        rows: return_path ? [return_path] : [],
        verified: props.domainStatus?.return_path_verified ?? false
      }
    ]
  }

  const current = diagnostic.value
  if (!current) return []

  const fromDiagnostic = (key: DnsDiagnosticRecord['key']): DnsRecord[] =>
    current.records
      .filter((record) => record.key === key)
      .map((record) => ({ hostname: record.hostname, type: record.type, value: record.expected }))

  return [
    { key: 'dkim', rows: fromDiagnostic('dkim'), verified: false },
    { key: 'spf', rows: fromDiagnostic('spf'), verified: false },
    { key: 'dmarc', rows: fromDiagnostic('dmarc'), verified: false },
    { key: 'return_path', rows: fromDiagnostic('return_path'), verified: false }
  ]
})

const displayRecords = computed(() =>
  recordGroups.value
    .map((group) => {
      const rows = group.rows.filter((row) => row?.hostname && row?.value).map(toDisplayRow)
      return {
        ...group,
        rows,
        state: worstState(rows),
        label: t(`pages.domain.config.steps.dns.records.${group.key}.label`),
        description: t(`pages.domain.config.steps.dns.records.${group.key}.description`)
      }
    })
    .filter((group) => group.rows.length > 0)
)

const stateTones: Record<DnsDiagnosticState, Tone> = {
  missing: { bg: 'bg-red-50', title: 'text-red-700', text: 'text-red-600', icon: 'text-red-600' },
  mismatch: { bg: 'bg-orange-50', title: 'text-orange-700', text: 'text-orange-600', icon: 'text-orange-600' },
  propagating: { bg: 'bg-blue-50', title: 'text-blue-700', text: 'text-blue-600', icon: 'text-blue-600' },
  ok: { bg: 'bg-green-50', title: 'text-green-700', text: 'text-green-600', icon: 'text-green-600' },
  unknown: {
    bg: 'bg-muted',
    title: 'text-foreground',
    text: 'text-foreground-secondary',
    icon: 'text-muted-foreground'
  }
}

const neutralTone: Tone = { bg: 'bg-muted', title: 'text-foreground', text: 'text-foreground-secondary', icon: '' }
const verifiedTone: Tone = stateTones.ok
const pendingTone: Tone = {
  bg: 'bg-orange-50',
  title: 'text-orange-700',
  text: 'text-orange-600',
  icon: 'text-orange-400'
}

const groupTone = (group: { state: DnsDiagnosticState | null; verified: boolean }): Tone => {
  if (group.state) return stateTones[group.state]
  if (!props.showStatus) return neutralTone
  return group.verified ? verifiedTone : pendingTone
}

const stateIcons = {
  missing: XCircle,
  mismatch: AlertTriangle,
  propagating: Clock,
  ok: CheckCircle2,
  unknown: HelpCircle
}

const stateChipColor: Record<DnsDiagnosticState, ChipColor> = {
  missing: 'red',
  mismatch: 'orange',
  propagating: 'blue',
  ok: 'green',
  unknown: 'gray'
}

const stateLabel = (state: DnsDiagnosticState) =>
  t(`pages.domain.config.steps.dns.diagnostic.states.${state}.short`, { provider: providerName.value })

const rowExplanation = (row: DisplayRow) => {
  if (!row.state) return ''
  if (row.state === 'mismatch' && row.hint) return t(`pages.domain.config.steps.dns.diagnostic.hints.${row.hint}`)
  return t(`pages.domain.config.steps.dns.diagnostic.states.${row.state}.description`, { provider: providerName.value })
}

const showStatusColumn = computed(() => diagnostic.value !== null)

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text)
    notification.create('success', {
      text: t('pages.domain.config.steps.dns.copied'),
      timeout: 2000
    })
  } catch {
    notification.create('error', {
      text: t('pages.domain.config.steps.dns.copyError'),
      timeout: 2000
    })
  }
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <div>
      <h3 class="text-lg font-semibold text-foreground">
        {{ t('pages.domain.config.steps.dns.title') }}
      </h3>
      <p class="text-sm text-muted-foreground mt-1">
        {{ t('pages.domain.config.steps.dns.description') }}
      </p>
    </div>

    <DnsDiagnosticSummary
      v-if="shouldDiagnose"
      :diagnostic="diagnostic"
      :is-loading="isDiagnosing"
      :has-failed="diagnosticFailed"
      @refresh="runDiagnostic"
    />

    <!-- Desktop: Table -->
    <div class="hidden sm:block">
      <div class="overflow-x-auto">
        <table class="w-full text-xs bg-muted border border-border">
          <thead>
            <tr class="border-b border-border">
              <th class="w-[100px] text-left py-3 px-2 font-medium text-foreground-secondary">Type</th>
              <th class="text-left py-3 px-2 font-medium text-foreground-secondary">
                {{ t('pages.domain.config.steps.dns.tableHeaders.name') }}
              </th>
              <th class="text-left py-3 px-2 font-medium text-foreground-secondary">
                {{ t('pages.domain.config.steps.dns.tableHeaders.value') }}
              </th>
              <th v-if="showStatusColumn" class="w-[220px] text-left py-3 px-2 font-medium text-foreground-secondary">
                {{ t('pages.domain.config.steps.dns.tableHeaders.status') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="record in displayRecords" :key="record.key">
              <tr class="border-b border-border">
                <td :colspan="showStatusColumn ? 4 : 3" class="py-2 px-2" :class="groupTone(record).bg">
                  <div class="flex items-center gap-2">
                    <component
                      :is="stateIcons[record.state]"
                      v-if="record.state"
                      class="w-4 h-4 min-w-4"
                      :class="groupTone(record).icon"
                    />
                    <template v-else-if="showStatus">
                      <BaseIcon
                        v-if="record.verified"
                        name="IconoirDoubleCheck"
                        class="w-4 h-4 min-w-4 text-green-600"
                      />
                      <Spinner v-else size="sm" class="min-w-4 m-0 text-orange-400" />
                    </template>
                    <p class="text-xs font-medium" :class="groupTone(record).title">{{ record.label }}</p>
                    <span class="text-xs" :class="groupTone(record).text">-</span>
                    <p class="text-xs" :class="groupTone(record).text">{{ record.description }}</p>
                  </div>
                </td>
              </tr>
              <tr v-for="(row, index) in record.rows" :key="`${record.key}-${index}`" class="border-b border-border">
                <td class="py-4 px-2 bg-white align-top">
                  <p class="font-medium">{{ row.type }}</p>
                </td>
                <td class="py-4 px-2 bg-white align-top">
                  <div class="flex items-center gap-2">
                    <Input
                      :id="`record-name-${record.key}-${index}-desktop`"
                      :model-value="row.hostname"
                      disabled
                      :name="`record-name-${record.key}-${index}-desktop`"
                      class="h-8 flex-1 min-w-[100px] text-xs"
                    />
                    <Button variant="outline" size="icon-sm" @click="copyToClipboard(row.hostname)">
                      <Copy />
                    </Button>
                  </div>
                </td>
                <td class="py-4 px-2 bg-white align-top">
                  <div class="flex items-center gap-2">
                    <Input
                      :id="`record-value-${record.key}-${index}-desktop`"
                      :model-value="row.value"
                      disabled
                      :name="`record-value-${record.key}-${index}-desktop`"
                      class="h-8 flex-1 min-w-[100px] text-xs"
                    />
                    <Button variant="outline" size="icon-sm" @click="copyToClipboard(row.value)">
                      <Copy />
                    </Button>
                  </div>
                </td>
                <td v-if="showStatusColumn" class="py-4 px-2 bg-white align-top">
                  <div v-if="row.state" class="flex flex-col gap-1">
                    <div>
                      <BaseChip :color="stateChipColor[row.state]" :text="stateLabel(row.state)" size="sm" />
                    </div>
                    <p class="text-xs text-foreground-secondary">{{ rowExplanation(row) }}</p>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Mobile: Cards -->
    <div class="flex flex-col gap-4 sm:hidden">
      <div v-for="record in displayRecords" :key="record.key" class="border border-border rounded-lg overflow-hidden">
        <div class="flex items-center gap-2 py-2 px-3" :class="groupTone(record).bg">
          <component
            :is="stateIcons[record.state]"
            v-if="record.state"
            class="w-4 h-4 min-w-4"
            :class="groupTone(record).icon"
          />
          <template v-else-if="showStatus">
            <BaseIcon v-if="record.verified" name="IconoirDoubleCheck" class="w-4 h-4 min-w-4 text-green-600" />
            <Spinner v-else size="sm" class="min-w-4 m-0 text-orange-400" />
          </template>
          <p class="text-xs font-medium" :class="groupTone(record).title">{{ record.label }}</p>
          <span class="text-xs" :class="groupTone(record).text">-</span>
          <p class="text-xs" :class="groupTone(record).text">{{ record.description }}</p>
        </div>

        <div
          v-for="(row, index) in record.rows"
          :key="`${record.key}-${index}`"
          class="p-3 bg-white flex flex-col gap-3 border-t border-border first:border-t-0"
        >
          <div v-if="row.state" class="flex flex-col gap-1">
            <div>
              <BaseChip :color="stateChipColor[row.state]" :text="stateLabel(row.state)" size="sm" />
            </div>
            <p class="text-xs text-foreground-secondary">{{ rowExplanation(row) }}</p>
          </div>

          <div class="flex flex-col gap-1">
            <span class="text-xs font-medium text-foreground-secondary">Type</span>
            <p class="text-sm font-medium text-foreground">{{ row.type }}</p>
          </div>

          <div class="flex flex-col gap-1">
            <span class="text-xs font-medium text-foreground-secondary">
              {{ t('pages.domain.config.steps.dns.tableHeaders.name') }}
            </span>
            <div class="flex items-center gap-2">
              <Input
                :id="`record-name-${record.key}-${index}-mobile`"
                :model-value="row.hostname"
                disabled
                :name="`record-name-${record.key}-${index}-mobile`"
                class="h-8 flex-1 text-xs"
              />
              <Button variant="outline" size="icon-sm" @click="copyToClipboard(row.hostname)">
                <Copy />
              </Button>
            </div>
          </div>

          <div class="flex flex-col gap-1">
            <span class="text-xs font-medium text-foreground-secondary">
              {{ t('pages.domain.config.steps.dns.tableHeaders.value') }}
            </span>
            <div class="flex items-center gap-2">
              <Input
                :id="`record-value-${record.key}-${index}-mobile`"
                :model-value="row.value"
                disabled
                :name="`record-value-${record.key}-${index}-mobile`"
                class="h-8 flex-1 text-xs"
              />
              <Button variant="outline" size="icon-sm" @click="copyToClipboard(row.value)">
                <Copy />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <BaseTip v-if="!shouldDiagnose && !isVerified" severity="warn" class="custom_message">
      <template #text>
        <div>
          <span class="font-medium">{{ t('pages.domain.config.steps.dns.propagationTitle') }}</span>
          <p class="mt-1">{{ t('pages.domain.config.steps.dns.propagationDescription') }}</p>
        </div>
      </template>
    </BaseTip>

    <div class="flex gap-3">
      <Button variant="outline" @click="$emit('back')">
        {{ t('pages.domain.config.buttons.back') }}
      </Button>
      <Button variant="default-primary" @click="$emit('finish')">
        {{ t('pages.domain.config.steps.dns.verifyButton') }}
      </Button>
    </div>
  </div>
</template>
